import { createSlice } from '@reduxjs/toolkit'
import { createAxiosThunk } from 'utils/reduxUtil'
import api from 'api/axios/foka'
import moment from 'moment'
import { isPresent } from 'utils'
import { CALENDAR_PRESETS } from 'components/calendar/calendarUtil'

export const SELECT_LAYOUT = 'SELECT_LAYOUT'
export const SEARCH_LAYOUT = 'SEARCH_LAYOUT'

const initialState = {
  isLoading: false,
  isDetailLoading: false,
  editlogs: [],
  selectedDetail: {
    hierarchy: {},
    historyDto: {},
  },
  channels: [],
  campaigns: [],
  hasCampaigns: false,
  groups: [],
  keywords: [],
  searchFormLayout: SELECT_LAYOUT,
  formValues: {
    actor: '',
    aggregateType: '',
    aggregateId: '',
    aggregateName: '',
    categories: {},
    startDt: moment().add(-6, 'day').format('YYYY-MM-DD'),
    endDt: moment().format('YYYY-MM-DD'),
    preset: CALENDAR_PRESETS.LAST_7_DAYS,
  },
  selectedAggregateType: null,
  page: 0,
  size: 10,
  total: 0,
  selectboxLoadingError: '',
  detailLoadingError: '',
  detailVisible: false,
}

export const fetchChannels = createAxiosThunk('editlog/fetchChannels', () =>
  api.ad.getBizChannels()
)

export const fetchCampaigns = createAxiosThunk(
  'editlog/fetchCampaigns',
  channel => {
    if (channel) {
      return api.ad.getBizChannelCampaigns(channel.id)
    } else {
      return api.ad.getCampaigns()
    }
  }
)

export const fetchGroups = createAxiosThunk('editlog/fetchGroups', campaignId =>
  api.ad.getCampaignAdGroups(campaignId)
)

export const fetchKeywords = createAxiosThunk(
  'editlog/fetchKeywords',
  adGroupId => api.ad.getAdGroupKeywords(adGroupId)
)

export const fetchEditlogs = createAxiosThunk(
  'editlog/fetchEditlogs',
  (noArgs, { getState }) => {
    const {
      page,
      size,
      selectedChannel,
      selectedCampaign,
      selectedGroup,
      selectedKeyword,
      selectedAggregateType,
      formValues: { startDt, endDt, actor, categories, aggregateName },
    } = getState().editlog

    let aggregateType = ''
    let aggregateId = ''
    if (getState().editlog.searchFormLayout === SELECT_LAYOUT) {
      if (selectedKeyword) {
        aggregateType = 'KEYWORD'
        aggregateId = selectedKeyword.id
      } else if (selectedGroup) {
        aggregateType = 'ADGROUP'
        aggregateId = selectedGroup.id
      } else if (selectedCampaign) {
        aggregateType = 'CAMPAIGN'
        aggregateId = selectedCampaign.id
      } else if (selectedChannel) {
        aggregateType = 'BIZCHANNEL'
        aggregateId = selectedChannel.id
      }
    } else {
      if (selectedAggregateType) {
        aggregateType = selectedAggregateType.id || ''
      }
    }
    const params = new URLSearchParams()
    if (isPresent(page)) params.append('page', page)
    if (isPresent(size)) params.append('size', size)
    if (isPresent(startDt)) params.append('startDt', startDt)
    if (isPresent(endDt)) params.append('endDt', endDt)
    if (isPresent(aggregateType)) params.append('aggregateType', aggregateType)
    if (isPresent(aggregateId)) params.append('aggregateId', aggregateId)
    if (isPresent(aggregateName)) params.append('aggregateName', aggregateName)
    if (isPresent(actor)) params.append('actor', actor)
    Object.entries(categories)
      .filter(c => c[1])
      .reduce((acc, curr) => [...acc, curr[0]], [])
      .forEach(c => params.append('categories', c))
    return api.editlog.getEditlogs(params)
  }
)

export const showEditlogDetail = createAxiosThunk(
  'editlog/fetchEditlogDetail',
  id => api.editlog.getEditlogDetail(id)
)

const editlogSlice = createSlice({
  name: 'editlog',
  initialState,

  reducers: {
    reset: () => initialState,
    refresh: state => ({
      ...initialState,
      searchFormLayout: state.searchFormLayout,
    }),
    setSearchFormLayout: (state, { payload: layout }) => {
      state.searchFormLayout = layout
      state.formValues.aggregateName = ''
      state.formValues.actor = ''
    },
    selectChannel: (state, { payload: channel }) => {
      state.selectedChannel = channel
    },
    selectCampaign: (state, { payload: campaign }) => {
      state.selectedCampaign = campaign
    },
    clearSelectedCampaign: state => {
      state.campaigns = []
      state.selectedCampaign = null
    },
    setCampaigns: (state, { payload: campaigns }) => {
      state.campaigns = campaigns
    },
    selectGroup: (state, { payload: group }) => {
      state.selectedGroup = group
    },
    clearSelectedGroup: state => {
      state.groups = []
      state.selectedGroup = null
    },
    selectKeyword: (state, { payload: keyword }) => {
      state.selectedKeyword = keyword
    },
    clearSelectedKeyword: state => {
      state.keywords = []
      state.selectedKeyword = null
    },
    inputActor: (state, { payload: actor }) => {
      state.formValues.actor = actor
    },
    inputQuery: (state, { payload: query }) => {
      state.formValues.aggregateName = query
    },
    selectAggregateType: (state, { payload: aggregateType }) => {
      state.selectedAggregateType = aggregateType
    },
    selectCategories: (state, { payload: categories }) => {
      state.formValues.categories = categories
      state.page = 0
    },
    setPage: (state, { payload: page }) => {
      state.page = page
    },
    setRange: (state, { payload: { start, end, presetValue } }) => {
      state.formValues.startDt = start
      state.formValues.endDt = end
      state.formValues.preset = presetValue
    },
    hideEditlogDetail: state => {
      state.detailVisible = false
    },
  },

  extraReducers: builder => {
    builder.addCase(fetchChannels.fulfilled, (state, { payload }) => {
      state.channels = payload.content.map(({ id, name, siteUrl }) => ({
        id,
        name,
        pcUrl:
          siteUrl.pcSiteYn === 'Y' &&
          `${siteUrl.pcUrl.protocol.toLowerCase()}://${siteUrl.pcUrl.url}`,
        mobileUrl:
          siteUrl.mobileSiteYn === 'Y' &&
          `${siteUrl.mobileUrl.protocol.toLowerCase()}://${
            siteUrl.mobileUrl.url
          }`,
      }))
    })

    builder.addCase(fetchChannels.rejected, (state, { error }) => {
      state.selectboxLoadingError = error
    })

    builder.addCase(fetchCampaigns.fulfilled, (state, { payload }) => {
      state.campaigns = payload.campaigns
      state.hasCampaigns = payload.campaigns.length > 0
    })

    builder.addCase(fetchCampaigns.rejected, (state, { error }) => {
      state.selectboxLoadingError = error
    })

    builder.addCase(fetchGroups.fulfilled, (state, { payload }) => {
      state.groups = payload.adGroupDtos.map(({ id, name, deletedDate }) => ({
        id,
        name,
        deletedDate,
      }))
    })

    builder.addCase(fetchGroups.rejected, (state, { error }) => {
      state.selectboxLoadingError = error
    })

    builder.addCase(fetchKeywords.fulfilled, (state, { payload }) => {
      state.keywords = payload.keywordDtos
    })

    builder.addCase(fetchKeywords.rejected, (state, { error }) => {
      state.selectboxLoadingError = error
    })

    builder.addCase(fetchEditlogs.pending, state => {
      state.isLoading = true
    })

    builder.addCase(fetchEditlogs.fulfilled, (state, { payload }) => {
      state.editlogs = payload.historyDtos.content
      state.total = payload.historyDtos.totalElements
      state.isLoading = false
    })

    builder.addCase(fetchEditlogs.rejected, state => {
      state.isLoading = false
    })

    builder.addCase(showEditlogDetail.pending, state => {
      state.isDetailLoading = true
    })

    builder.addCase(showEditlogDetail.fulfilled, (state, { payload }) => {
      state.selectedDetail = payload
      state.isDetailLoading = false
      state.detailVisible = true
    })

    builder.addCase(showEditlogDetail.rejected, (state, { error }) => {
      state.detailLoadingError = error
      state.isDetailLoading = false
    })
  },
})

export const {
  reset,
  refresh,
  setSearchFormLayout,
  selectChannel,
  selectCampaign,
  clearSelectedCampaign,
  setCampaigns,
  selectGroup,
  clearSelectedGroup,
  selectKeyword,
  clearSelectedKeyword,
  selectAggregateType,
  selectCategories,
  inputActor,
  inputQuery,
  setPage,
  setRange,
  hideEditlogDetail,
} = editlogSlice.actions
export default editlogSlice.reducer
